import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { belowElement, type OpenRequest } from "./anchor";
import type { LongPress } from "./useLongPress";
import { isContextMenuKey, isEditable, isKeyboardContextMenu } from "./triggerTarget";

/** What the `contextmenu` and key handlers of a trigger area need. */
export interface TriggerContext<T> {
  disabled: boolean;
  resolve: (element: Element) => T | null;
  open: (target: T, request: OpenRequest) => void;
  longPress: Pick<LongPress, "cancel" | "firedRecently">;
}

/** Selector of the menu panels: their own right-clicks never reach the browser's menu. */
const MENU_PANEL_SELECTOR = ".nb-ctx";

/** Events from portals (dialogs, the menu itself) bubble through React; only our own DOM is handled. */
function isFromPortal(e: ReactMouseEvent<HTMLElement>, el: Element): boolean {
  return !e.currentTarget.contains(el);
}

function quietMenuPanel(e: ReactMouseEvent<HTMLElement>, el: Element): void {
  if (el.closest?.(MENU_PANEL_SELECTOR)) e.preventDefault();
}

/** Keyboard-fired events open at the focused element inside the area; right-clicks at the pointer. */
function openRequestFor(e: ReactMouseEvent<HTMLElement>): OpenRequest {
  const focused = document.activeElement;
  if (isKeyboardContextMenu(e) && focused instanceof HTMLElement && e.currentTarget.contains(focused)) return belowElement(focused);
  return { point: { x: e.clientX, y: e.clientY } };
}

export function handleContextMenu<T>(e: ReactMouseEvent<HTMLElement>, ctx: TriggerContext<T>): void {
  const el = e.target as Element;
  if (isFromPortal(e, el)) {
    quietMenuPanel(e, el);
    return;
  }
  if (isEditable(el)) return;
  if (ctx.disabled || ctx.longPress.firedRecently()) {
    e.preventDefault();
    return;
  }
  const target = ctx.resolve(el);
  if (target === null) return;
  e.preventDefault();
  ctx.longPress.cancel();
  ctx.open(target, openRequestFor(e));
}

export function handleTriggerKeyDown<T>(e: ReactKeyboardEvent<HTMLElement>, ctx: TriggerContext<T>): void {
  if (!isContextMenuKey(e)) return;
  const el = e.target as Element;
  if (!(el instanceof HTMLElement) || !e.currentTarget.contains(el) || isEditable(el) || ctx.disabled) return;
  const target = ctx.resolve(el);
  if (target === null) return;
  e.preventDefault();
  e.stopPropagation();
  ctx.open(target, belowElement(el));
}
